/** Reference:
 * https://www.webrtc-experiment.com/RecordRTC/simple-demos/
 * https://www.webrtc-experiment.com/RecordRTC/simple-demos/video-recording.html
 * https://github.com/muaz-khan/RecordRTC/blob/master/simple-demos/video-recording.html */

import React, { useRef } from 'react';
import { useCameraStream } from './hooks/useCameraStream';
import { useVideoRecorder } from './hooks/useVideoRecorder';
import { usePhotoCapture } from './hooks/usePhotoCapture';
import LoadingOverlay from './LoadingOverlay';
import type { RecorderProps } from './types';

const Recorder = ({
    initialFacing,
    recordRTCOptions,
    onRecordingComplete,
    onRecordingStateChanged,
    onPhotoCapture,
    onPermissionChange,
    className,
    style,
}: RecorderProps = {}) => {
    const streamedVideoRef = useRef<HTMLVideoElement | null>(null);

    const {
        camera,
        permission,
        facing,
        isLoading,
        canFlipCamera,
        toggleFacing,
        cycleCamera,
        destroyCamera,
        enableCamera,
    } = useCameraStream(streamedVideoRef, initialFacing, onPermissionChange);

    const {
        isRecording,
        isRecorded,
        isPaused,
        recordURL,
        recordMimeType,
        startRecording,
        stopRecording,
        pauseRecording,
        resumeRecording,
        retry,
        play,
    } = useVideoRecorder(camera, streamedVideoRef, {
        recordRTCOptions,
        onRecordingComplete,
        onStateChanged: onRecordingStateChanged,
    });

    const recordExtension = recordMimeType?.split(';')[0].split('/')[1] || 'webm';

    const { photoURL, takePhoto } = usePhotoCapture(streamedVideoRef, onPhotoCapture);

    return (
        <React.Fragment>
            <div
                className={className}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    position: 'relative',
                    zIndex: '1',
                    overflowY: 'scroll',
                    width: '100%',
                    height: '100%',
                    ...style,
                }}
            >
                <h3>Mode: {facing} (force flipping doesn't change this)</h3>
                <video style={{
                    width: '90%',
                    maxWidth: '960px',
                    transform: facing === 'user' ? 'scaleX(-1)' : undefined,
                }}
                       autoPlay
                       playsInline
                       ref={streamedVideoRef}
                />
                {isRecorded ?
                    <React.Fragment>
                        <button onClick={play}>Play</button>
                        <button onClick={retry}>Retry</button>
                        {recordURL ? <a href={recordURL} download={`recording.${recordExtension}`}>Download</a> : null}
                    </React.Fragment> :
                    <React.Fragment>
                        {isRecording ?
                            <React.Fragment>
                                {isPaused ?
                                    <button onClick={resumeRecording}>Resume</button> :
                                    <button onClick={pauseRecording}>Pause</button>}
                                <button onClick={stopRecording}>Stop</button>
                            </React.Fragment> :
                            camera ?
                                <React.Fragment>
                                    {canFlipCamera ? <button onClick={toggleFacing}>Flip Camera</button> : null}
                                    {canFlipCamera ? <button onClick={cycleCamera}>Force Flip Camera</button> : null}
                                    <button onClick={() => startRecording()}>Record</button>
                                    <button onClick={destroyCamera}>destroy camera</button>
                                    <button onClick={takePhoto}>Take Photo</button>
                                </React.Fragment> :
                                <button onClick={enableCamera}>Enable Camera</button>
                        }
                    </React.Fragment>}
                <div>
                    <span>
                        {!permission.status ? 'failed to load camera' : null}
                    </span>
                </div>
                <div>
                    {photoURL ? (
                        <img style={{
                            transform: facing === 'user' ? 'scaleX(-1)' : undefined,
                            width: '90%',
                            maxWidth: '960px',
                        }}
                             src={photoURL}
                             alt="Captured frame"
                        />
                    ) : null}
                </div>
            </div>
            {isLoading ? <LoadingOverlay /> : null}
        </React.Fragment>
    )
}

export default Recorder;
